import { useCallback, useEffect, useRef, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";
import io, { type Socket } from "socket.io-client";

import { siteSessionTokenKeyName } from "@/config/environment";
import { invalidateReconciliationQueries } from "@/hooks/useReconciliationQueryInvalidation";
import useAuth from "@/modules/auth/useAuth";
import { getCookie } from "@/tools/cookies";
import { subscribeToReconciliationUpdates } from "@/utils/reconciliationBroadcast";

import {
  ProvisionalStatus,
  ReconciliationServiceContext,
} from "./ReconciliationServiceContext";

export { ReconciliationServiceContext };

const OPENSEARCH_SYNC_DELAY_MS = 2_000;

interface SocketMessageParams {
  status: string;
  requestId: string;
  subsourceId: string;
  userId: string;
  version: string;
  startedAt: string;
  state: string;
  timeStart: string;
  timeEnd: string;
  progress: string;
  result: string;
  completedAt: string;
  elapsedTime: string;
  errorMessage?: string;
  uploadId?: string;
  message?: string;
}

interface IBlockedTenant {
  tenantId: string;
  blocked: boolean;
  service: string;
}

export interface ReconciliationServiceContextProps {
  children: React.ReactNode;
}

export default function ReconcilliationServiceProvider({
  children,
}: ReconciliationServiceContextProps) {
  const [working, setWorking] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [uploadId, setUploadId] = useState<string>();
  const [uploadMessage, setUploadMessage] = useState<string>();
  const [isConnected, setIsConnected] = useState(false);
  const [lastError, setLastError] = useState<string>();
  const [logicalDelete, setLogicalDelete] = useState(false);
  const [blockedTenant, setBlockedTenant] = useState<IBlockedTenant | null>(
    null,
  );
  const [provisionalStatus, _setProvisionalStatus] =
    useState<ProvisionalStatus>({
      isProvisional: false,
    });
  const { profile } = useAuth();
  const socketRef = useRef<Socket | undefined>(undefined);
  const activeSubsourceIdRef = useRef<string | undefined>(undefined);
  const queryClient = useQueryClient();

  useEffect(() => {
    return subscribeToReconciliationUpdates(() => {
      void invalidateReconciliationQueries(queryClient, {
        includeSuggested: true,
      });
    });
  }, [queryClient]);

  useEffect(() => {
    if (import.meta.env.VITE_REALTIME_ON === "false") {
      return;
    }

    const socket = io(import.meta.env.VITE_REALTIME_URL, {
      extraHeaders: {
        authorization: siteSessionTokenKeyName
          ? `Bearer ${getCookie(siteSessionTokenKeyName)}`
          : "",
      },
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 10000,
    });
    let wasEngineWorking = false;
    let reconciliationInvalidationTimeout: ReturnType<
      typeof setTimeout
    > | null = null;

    // Connection events
    socket.on("connect", () => {
      setIsConnected(true);
      setLastError(undefined);
      console.log("Socket connected");
    });

    socket.on("disconnect", (reason) => {
      setIsConnected(false);
      console.log(`Socket disconnected: ${reason}`);
    });

    socket.on("connect_error", (error) => {
      setIsConnected(false);
      setLastError(error.message);
      console.error("Socket connection error:", error);
    });

    socket.on("reconnect_attempt", (attemptNumber) => {
      console.log(`Reconnection attempt ${attemptNumber}`);
    });

    socket.on("reconnect_failed", () => {
      setLastError("Failed to reconnect after maximum attempts");
      console.error("Socket reconnection failed");
    });

    // Business events
    socket.on("banner", (message: string) => {
      const isEngineWorking = message === "true";

      if (isEngineWorking) {
        if (reconciliationInvalidationTimeout !== null) {
          clearTimeout(reconciliationInvalidationTimeout);
          reconciliationInvalidationTimeout = null;
        }
        wasEngineWorking = true;
        setWorking(true);
        return;
      }

      if (wasEngineWorking) {
        wasEngineWorking = false;
        if (reconciliationInvalidationTimeout !== null) {
          clearTimeout(reconciliationInvalidationTimeout);
        }
        reconciliationInvalidationTimeout = setTimeout(() => {
          void invalidateReconciliationQueries(queryClient, {
            includeSuggested: true,
          })
            .finally(() => {
              if (!wasEngineWorking) {
                setWorking(false);
              }
              reconciliationInvalidationTimeout = null;
            })
            .catch(() => undefined);
        }, OPENSEARCH_SYNC_DELAY_MS);
      }
    });

    socket.on("upload", (message: SocketMessageParams) => {
      if (message.userId === profile.id) {
        setUploadId(message.uploadId);
      }
    });

    socket.on("confirm", (message: SocketMessageParams) => {
      if (message.userId === profile.id) {
        setProcessing(message.state === "true");
        // When processing completes (state === "false"), capture the result message
        if (message.state === "false" && message.message) {
          setUploadMessage(message.message);
        }
      }
    });

    socket.on("logicalDelete", (message: SocketMessageParams) => {
      if (message.userId === profile.id) {
        setLogicalDelete((prev) => {
          const isDeleting = message.state === "true";

          if (prev && !isDeleting) {
            console.log(
              "[logical-delete-sync] completed, resetting cached tables",
            );
            void invalidateReconciliationQueries(queryClient);
          }

          return isDeleting;
        });
      }
    });

    socket.on("blockedTenant", (message: IBlockedTenant) => {
      setBlockedTenant((prev) => {
        const isBlocked = message.blocked;
        if (prev?.blocked && !isBlocked) {
          queryClient.invalidateQueries();
        }
        return message;
      });
    });

    socketRef.current = socket;

    // Cleanup function
    return () => {
      if (reconciliationInvalidationTimeout !== null) {
        clearTimeout(reconciliationInvalidationTimeout);
      }
      if (socket) {
        socket.removeAllListeners();
        socket.disconnect();
      }
    };
  }, [profile.id, queryClient]);

  const resetUploadId = useCallback(() => {
    setUploadId(undefined);
    setUploadMessage(undefined);
  }, []);

  const setActiveSubsourceId = useCallback(
    (subsourceId: string | undefined) => {
      activeSubsourceIdRef.current = subsourceId;
    },
    [],
  );

  return (
    <ReconciliationServiceContext.Provider
      value={{
        working,
        uploadId,
        uploadMessage,
        isConnected,
        lastError,
        resetUploadId,
        processing,
        logicalDelete,
        blockedTenant,
        provisionalStatus,
        // Filter for movements subsourceId
        setActiveSubsourceId,
      }}
    >
      {children}
    </ReconciliationServiceContext.Provider>
  );
}
